<script setup lang="ts">
import { computed } from 'vue';
import {
  APP,
  HUB_HOME,
  HUB_SETTINGS,
  SIDE_MENU_PRODUCTS,
  USER_RIGHTS
} from '@base-shared/utils/constants';
import IconSvg from '@base-shared/components/IconSvg.vue';

const props = withDefaults(defineProps<{
  product?: APP;
  type?: 'column' | 'row';
  userRights?: string[];
  userEnabledProducts?: string[];
  isLoading?: boolean;
}>(), {
  type: 'column'
});

defineEmits<{
  (e: 'productSelect', product: string): void;
}>();

const availableProducts = computed(() => {
  const enabledProducts = props.userEnabledProducts ?? [];
  const userRights = props.userRights ?? [];
  const isGlobalAdmin = userRights.includes(USER_RIGHTS.GLOBAL_ADMIN);

  const otherProductsEnabled = [
    // Include Hub settings ONLY if product is not HUB Settings AND if user is Global Admin
    ...(props.product !== APP.HUB_SETTINGS && isGlobalAdmin ? [HUB_SETTINGS] : []),
    // Include Hub Home if product is not Hub Home
    ...(props.product !== APP.HUB ? [HUB_HOME] : [])
  ];

  const productList = [
    // Get side menu products based on enabled products for a user
    ...SIDE_MENU_PRODUCTS.filter((product) => enabledProducts.includes(product.value)),
    // Include other products enabled
    ...otherProductsEnabled
  ];

  // Filter productList to not include from which product user is accessing from
  return productList.filter((product) => product.value !== props.product);
});
</script>

<template>
  <div
    :class="{
      'product-select-nav flex items-center gap-[18px] mt-6': true,
      'flex-col': type === 'column',
      'justify-center': type === 'row',
      'animate-pulse': isLoading,
    }"
  >
    <template v-if="isLoading">
      <button
        v-for="item in 3"
        :key="item"
        class="product-select-nav-item flex items-center justify-center w-[91px] h-[91px] rounded-lg cursor-pointer select-none bg-gray-400 flex-col"
        :class="{ 'shadow-md': type === 'row' }"
        data-component-element-id="product-select-nav-item-loading"
      >
        <div class="w-1/2 h-1.5 bg-gray-200 rounded-full dark:bg-gray-700 max-w-[480px] mt-10" />
      </button>
    </template>
    <template v-else-if="type === 'row' && !availableProducts?.length">
      <div class="flex flex-col items-center justify-center gap-[10px]">
        <slot name="no-products-placeholder" />
      </div>
    </template>
    <template v-else>
      <button
        v-for="availableProduct in availableProducts"
        :key="availableProduct.value"
        class="product-select-nav-item px-2 flex items-center justify-center w-[91px] h-[91px] rounded-lg cursor-pointer select-none"
        :data-component-element-id="`product-select-nav-item-${availableProduct.value}`"
        :class="[availableProduct.value, { 'shadow-md': type === 'row' }]"
        @click="$emit('productSelect', availableProduct.value)"
      >
        <IconSvg
          class="w-auto! h-[57px]!"
          :name="availableProduct.iconWithName"
        />
      </button>
    </template>
  </div>
</template>

<style lang="css" scoped>
@reference '@base-shared/assets/css/tailwind.css';

.product-select-nav {
  .product-select-nav-item {
    @apply border-none;

    &.arya {
      @apply bg-arya-500 hover:bg-arya-600 text-white;
    }

    &.assets {
      @apply bg-assets-500 hover:bg-assets-600 text-spinetix-900!;
    }

    &.activate {
      @apply bg-primary-800 hover:bg-primary-900 text-white!;
    }

    &.integrations {
      @apply bg-integrations-500 hover:bg-integrations-600 text-spinetix-900!;
    }

    &.hub-settings, &.hub {
      @apply bg-white hover:bg-gray-300 text-black!;
    }
  }
}
</style>
